import { Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import FooterSection from '../../components/FooterSection';
import { WA_NUMBER } from '../../constant/detail';

const relatedServices = [
  { label: 'Jasa Maintenance Kolam Renang Bali', to: '/jasa-maintenance-kolam-renang-bali' },
  { label: 'Jual Chemical Pool Bali', to: '/jual-chemical-pool-bali' },
  { label: 'Pasang Filter Kolam Renang Bali', to: '/pasang-filter-kolam-renang-bali' },
  { label: 'Jasa Testkit Air Kolam Bali', to: '/jasa-testkit-air-kolam-bali' },
  { label: 'Konsultasi Kolam Renang Bali', to: '/konsultasi-kolam-renang-bali' },
];

const WA_MESSAGE = encodeURIComponent('Hello, I would like to inquire about pool equipment in Bali');

const EquipmentPool = () => {
  return (
    <>
      <Head>
        <title>Jual Equipment Pool Bali | Pompa &amp; Perlengkapan Kolam | SJT</title>
        <meta
          name="description"
          content="Jual equipment pool Bali: pompa, filter, skimmer, lampu LED kolam. Spare part lengkap, teknisi berpengalaman. Garansi resmi distributor. Hubungi kami sekarang!"
        />
        <link rel="canonical" href="https://poolshopsuryajayateknik.com/equipment-pool-bali" />
        <meta property="og:title" content="Jual Equipment Pool Bali | Pompa & Perlengkapan Kolam | SJT" />
        <meta property="og:description" content="Jual equipment pool Bali: pompa, filter, skimmer, lampu LED kolam. Spare part lengkap, teknisi berpengalaman. Garansi resmi distributor." />
        <meta property="og:image" content="https://poolshopsuryajayateknik.com/img/blog/img-2.jpg" />
        <meta property="og:url" content="https://poolshopsuryajayateknik.com/equipment-pool-bali" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Jual Equipment Pool Bali | Pompa & Perlengkapan Kolam | SJT" />
        <meta name="twitter:description" content="Jual equipment pool Bali: pompa, filter, skimmer, lampu LED kolam. Spare part lengkap, teknisi berpengalaman. Garansi resmi distributor." />
      </Head>

      <main className="mt-16 bg-white text-gray-800">

        {/* Hero */}
        <section className="bg-tertiary text-white py-16 px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
              Pool Equipment Bali – Complete, Original &amp; Guaranteed
            </h1>
            <p className="text-lg text-primary/80 mb-8">
              Reliable pool equipment is the key to a pool that performs well and lasts long.
              Suryajaya Teknik provides sales and repair services for complete pool equipment in
              Bali, from circulation pumps and filters to skimmers and LED underwater lights. All
              our products are original with official warranty from distributors, so you can invest
              with confidence for the long term. Our experienced technician team is ready to help
              with installation, maintenance, and repairs of your pool equipment.
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:opacity-90 text-tertiary font-semibold py-3 px-8 rounded-full text-lg transition"
            >
              Check Stock &amp; Price via WhatsApp
            </a>
          </div>
        </section>

        {/* Why */}
        <section className="max-w-4xl mx-auto py-14 px-6">
          <h2 className="text-2xl font-bold text-tertiary mb-4">
            Quality Pool Equipment for Optimal Performance
          </h2>
          <p className="mb-4 leading-relaxed">
            Investing in quality pool equipment will save on operational and maintenance costs in
            the long run. Reliable pumps and filters ensure water circulation runs efficiently,
            reducing chemical usage. High-efficiency equipment also contributes to significant
            electricity bill savings, especially for pools that operate throughout the day. In
            Bali's tropical climate, pool equipment must be able to work in hot and humid conditions
            without significant performance drops. Choosing products from trusted brands with good
            after-sales support is a smart investment decision.
          </p>
          <p className="leading-relaxed">
            As a pool equipment supplier in Bali, we only sell products we have selected based on
            quality, reliability, and spare part availability. Our experience installing and
            maintaining hundreds of pools in Bali gives us a deep understanding of which brands
            and models work best for local Bali conditions. We also provide equipment selection
            consultation tailored to your pool size, usage frequency, and budget. By buying from
            us, you get not just a product, but full technical support from an experienced team.
          </p>
        </section>

        {/* Products */}
        <section className="bg-primary/10 py-14 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-tertiary mb-6">Pool Equipment We Carry</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {[
                { name: 'Circulation Pumps', desc: 'Various capacities (0.5 HP - 3 HP) from leading brands. Energy-efficient, quiet, and durable for residential and commercial pools.' },
                { name: 'Sand Filter & Housing', desc: 'Filter tanks in various sizes (12" - 36") with multiport valve. Made from fiberglass or stainless steel.' },
                { name: 'Skimmer & Main Drain', desc: 'Surface skimmers and main drains for optimal circulation. Available in various models for in-ground and above-ground installation.' },
                { name: 'LED Underwater Lights', desc: 'Waterproof LED pool lighting with various color options and light effects for a stunning pool aesthetic.' },
                { name: 'Chlorinator & Dosing Pump', desc: 'Automated dosing system for precise and consistent chemical management. Ideal for hotel and villa pools requiring strict control.' },
                { name: 'Valves & Pipe Accessories', desc: 'Pipes, butterfly valves, check valves, and fittings in various sizes for a perfect circulation system installation.' },
              ].map((prod, i) => (
                <div key={i} className="bg-white border border-primary rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold text-tertiary mb-2">{prod.name}</h3>
                  <p className="text-gray-600 text-sm">{prod.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service */}
        <section className="max-w-4xl mx-auto py-14 px-6">
          <h2 className="text-2xl font-bold text-tertiary mb-4">Service &amp; Repair</h2>
          <ul className="space-y-3">
            {[
              'On-site diagnosis of pool pump and motor damage',
              'Replacement of worn pump bearings, seals, and impellers',
              'Repair and replacement of filter multiport valves',
              'Full equipment installation including wiring and plumbing',
              'Calibration and reconfiguration of automated chemical dosing systems',
              'Replacement of dead or dim LED underwater lights',
              'Repair of leaks in pump body and pipe fittings',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-tertiary font-bold mt-0.5">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section className="bg-tertiary text-white py-16 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">Need Pool Equipment in Bali?</h2>
            <p className="text-primary/80 mb-8">
              Contact us to check stock availability, get a price estimate, or schedule a technician
              visit to diagnose your pool equipment. We are ready to help you find the best solution
              at a competitive price.
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:opacity-90 text-tertiary font-semibold py-3 px-10 rounded-full text-lg transition"
            >
              Contact Us via WhatsApp
            </a>
          </div>
        </section>

        {/* Related Services */}
        <section className="max-w-4xl mx-auto py-10 px-6">
          <h2 className="text-lg font-bold text-tertiary mb-4">Layanan Lainnya</h2>
          <div className="flex flex-wrap gap-3">
            {relatedServices.map(({ label, to }) => (
              <Link key={to} to={to} className="text-sm font-medium text-primary border border-primary/40 px-4 py-2 rounded-full hover:bg-primary/10 transition">
                {label}
              </Link>
            ))}
          </div>
        </section>

        <FooterSection />
      </main>
    </>
  );
};

export default EquipmentPool;
